/**
 * Підбір коду КАТОТТГ (рішення власника 2026-08-18).
 *
 * Юзер може вписати код руками (він є у витягу/звіті) — поле лишається
 * вільним; поруч кнопка «підібрати» → попап з полями пошуку (назва
 * населеного пункту + область + тип). Код підставляється ТОЧНИЙ з
 * державного кодифікатора — вгадувати з тексту адреси не можна (десятки
 * однойменних сіл, а код іде у звітність ДПС).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { signalUserEdit } from '@/store/formDrafts';

interface KatottgRow {
  code: string;
  level: number;
  category: string;
  category_label: string;
  name: string;
  region_name: string | null;
  district_name: string | null;
  hromada_name: string | null;
  full_path: string;
}

// Порядок і склад — рішення власника 2026-08-18: спершу ОБРАТИ ТИП
// (без «усі» — інакше у видачі чехарда з 30 тис. записів), «місто зі
// спец. статусом» прибрано (Київ/Севастополь у юрадресі — це РАЙОН
// міста, а сам Київ шукається як «місто»).
const TYPES: { v: string; label: string }[] = [
  { v: 'B', label: 'район міста (Київ, Харків…)' },
  { v: 'M', label: 'місто' },
  { v: 'T', label: 'селище міського типу' },
  { v: 'C', label: 'село' },
  { v: 'X', label: 'селище' },
];

export default function KatottgPicker({ onPick }: {
  onPick: (code: string, row: KatottgRow) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('');
  const [category, setCategory] = useState('');  // '' = ще не обрано

  const regionsQ = useQuery({
    queryKey: ['katottg-regions'],
    queryFn: () => api.get<{ items: { code: string; name: string }[] }>(
      '/katottg/regions').then((r) => r.data.items),
    enabled: open,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const listQ = useQuery({
    queryKey: ['katottg', q, region, category],
    queryFn: () => api.get<{ items: KatottgRow[] }>('/katottg', {
      params: { q, region, category, limit: 40 },
    }).then((r) => r.data.items),
    enabled: open && !!category && (q.trim().length >= 2 || !!region),
  });

  return (
    <>
      <button type="button" style={{ padding: '0.15rem 0.5rem', fontSize: '0.78rem' }}
        title="Підібрати код за населеним пунктом (державний кодифікатор)"
        onClick={() => setOpen(true)}>
        підібрати
      </button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setOpen(false)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(720px, 94vw)',
            maxHeight: '88vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Підбір коду КАТОТТГ</h3>

            <div className="doc-grid" style={{ alignItems: 'flex-end' }}>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>1. Тип <span style={{ color: '#dc2626' }}>*</span></label>
                <select value={category} autoFocus
                  onChange={(e) => setCategory(e.target.value)}>
                  <option value="">— оберіть тип —</option>
                  {TYPES.map((t) => (
                    <option key={t.v} value={t.v}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>2. Назва</label>
                <input value={q} disabled={!category}
                  placeholder={category
                    ? (category === 'B' ? 'напр. Голосіївський' : 'напр. Ірпінь')
                    : 'спершу оберіть тип'}
                  onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>3. Область</label>
                <select value={region} disabled={!category}
                  onChange={(e) => setRegion(e.target.value)}>
                  <option value="">усі області</option>
                  {(regionsQ.data ?? []).map((r) => (
                    <option key={r.code} value={r.name}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginTop: '0.4rem',
              border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}>
              {!category ? (
                <div style={{ padding: '0.8rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                  Оберіть <b>тип</b> — далі введіть назву або оберіть область.
                </div>
              ) : q.trim().length < 2 && !region ? (
                <div style={{ padding: '0.8rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                  Введіть назву (від 2 літер) або оберіть область.
                </div>
              ) : listQ.isLoading ? (
                <div style={{ padding: '0.8rem', color: 'var(--muted)' }}>Пошук…</div>
              ) : (listQ.data ?? []).length === 0 ? (
                <div style={{ padding: '0.8rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                  Нічого не знайдено. Спробуйте іншу назву або зніміть фільтри.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <tbody>
                    {(listQ.data ?? []).map((r) => (
                      <tr key={r.code} style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer' }}
                        onClick={(e) => { signalUserEdit(e.currentTarget); onPick(r.code, r); setOpen(false); }}>
                        <td style={{ padding: '0.35rem 0.5rem' }}>
                          <b>{r.name}</b>
                          <span style={{ color: 'var(--muted)' }}> — {r.category_label}</span>
                          <div style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
                            {r.full_path}
                          </div>
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right',
                          whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                          {r.code}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginTop: '0.7rem', gap: '0.5rem' }}>
              <small style={{ color: 'var(--muted)' }}>
                Клік по рядку — підставити код. У великих містах (Київ,
                Харків…) в адресі зазвичай стоїть код <b>району міста</b>.
              </small>
              <button onClick={() => setOpen(false)}>закрити</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
